/**
 * Occasion packs (D-180) on this phone. GET /api/occasions gives the current occasion (if one runs) and the ones the
 * player owns; the last answer is cached on this device so the first paint already
 * wears the occasion. Other players' occasion frames and emoji can name an occasion we have never seen: `fetchOccasion`
 * asks GET /api/occasions/:id once and keeps the answer in memory (null when it failed, so it is never asked again).
 *
 * The look: while an occasion is current and the player keeps occasion looks on, its colour tokens go over the active
 * theme (the dark set over dark themes, the light set over Sea glass and Reed) in one <style> element, scoped to
 * html[data-occasion]. Every value is re-checked here against strict patterns, so nothing but colours and our own art
 * URLs ever reaches CSS.
 */
import { useEffect, useState } from "react";
import { DEFAULT_TABLE_DESIGN, EMOJI_MOTIONS, OCCASION_TOKENS, isOccasionDesign, occasionOfDesign, tableDesign, type EmojiMotion, type OccasionView, type TableDesign } from "@digu/shared";
import { LIGHT_THEMES } from "./themes";

const CACHE_KEY = "digu.occasions";
const STYLE_ID = "digu-occasion-look";
const EVENT = "digu:occasions";
const ID = /^[a-z][a-z0-9-]{2,31}$/;
const HEX = /^#[0-9a-fA-F]{6}$/;
const CSS_COLOUR = /^(#[0-9a-fA-F]{6}|rgba?\(\s*[0-9 .,/%]+\))$/;
const ART_URL = /^\/(art|api)\/[A-Za-z0-9/_.\-?=%]+$/;
/** Themes whose base is light; every other built-in (Classic, Ink, Lagoon, After hours) is dark. */
const LIGHT_BASES = LIGHT_THEMES;
/** D-210: themes an occasion's colours never touch. */
const CLASSIC_THEMES: ReadonlySet<string> = new Set(["classic", "classic-light"]);

export interface OccasionsState { current: OccasionView | null; owned: OccasionView[] }

/** A view we can draw safely, or null: ids, colours and art URLs must all match the strict patterns. */
export function safeOccasion(v: unknown): OccasionView | null {
  if (!v || typeof v !== "object") return null;
  const o = v as OccasionView;
  try {
    if (typeof o.id !== "string" || !ID.test(o.id) || typeof o.name !== "string") return null;
    const arts = [o.table.art.room, o.table.art.rim, o.table.art.felt, o.back.image, ...o.emoji.items.map((e) => e.image), ...(o.home.banner ? [o.home.banner.image] : [])];
    if (!arts.every((u) => typeof u === "string" && ART_URL.test(u))) return null;
    const colours = [o.table.tokens.line, o.table.tokens.stitch, o.table.tokens.shade, ...(o.table.tokens.glint ? [o.table.tokens.glint] : []), o.table.swatch.felt, o.table.swatch.rim];
    if (!colours.every((c) => typeof c === "string" && CSS_COLOUR.test(c))) return null;
    if (!Array.isArray(o.frame.colours) || !Array.isArray(o.look.bunting)) return null;
    const hexOnly = (list: string[]) => list.filter((c) => typeof c === "string" && HEX.test(c));
    const motion = (m: string): EmojiMotion => ((EMOJI_MOTIONS as readonly string[]).includes(m) ? (m as EmojiMotion) : "pop");
    return {
      ...o,
      look: { dark: o.look.dark ?? {}, light: o.look.light ?? {}, bunting: hexOnly(o.look.bunting).slice(0, 4) },
      frame: { ...o.frame, colours: hexOnly(o.frame.colours).slice(0, 4) },
      emoji: { ...o.emoji, items: o.emoji.items.filter((e) => typeof e.id === "string" && /^[a-z][a-z0-9]{1,23}$/.test(e.id)).map((e) => ({ ...e, motion: motion(e.motion) })) },
    };
  } catch { return null; }
}

function parseState(body: unknown): OccasionsState | null {
  if (!body || typeof body !== "object") return null;
  const b = body as { current?: unknown; owned?: unknown };
  if (!("current" in b) || !Array.isArray(b.owned)) return null;
  return { current: b.current ? safeOccasion(b.current) : null, owned: b.owned.map(safeOccasion).filter((x): x is OccasionView => !!x) };
}

function readCache(): OccasionsState {
  try { const raw = localStorage.getItem(CACHE_KEY); return (raw && parseState(JSON.parse(raw))) || { current: null, owned: [] }; } catch { return { current: null, owned: [] }; }
}
function writeCache(s: OccasionsState): void { try { localStorage.setItem(CACHE_KEY, JSON.stringify(s)); } catch { /* private mode */ } }

let state: OccasionsState = readCache();
/** Occasions fetched one by one (other players' frames and emoji): null when the server had none. */
const fetched = new Map<string, OccasionView | null>();
const inFlight = new Map<string, Promise<OccasionView | null>>();
let looksOn = true;

const emit = (): void => { if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENT)); };

/** For tests: forget everything (the cache, fetched occasions and the applied look). */
export function resetOccasions(): void {
  state = { current: null, owned: [] }; fetched.clear(); inFlight.clear(); looksOn = true;
  try { localStorage.removeItem(CACHE_KEY); } catch { /* private mode */ }
  if (typeof document !== "undefined") { delete document.documentElement.dataset.occasion; const s = document.getElementById(STYLE_ID); if (s) s.textContent = ""; }
}

export const occasionsState = (): OccasionsState => state;

/** GET /api/occasions, cached on this phone; re-applies the look (the current occasion may have changed). */
export async function loadOccasions(): Promise<OccasionsState> {
  try {
    const r = await fetch("/api/occasions", { credentials: "same-origin" });
    if (!r || !r.ok) return state;
    const next = parseState(JSON.parse(await r.text()));
    if (!next) return state;
    state = next;
    writeCache(state);
    applyOccasionLook(looksOn);
    emit();
  } catch { /* offline: keep the cache */ }
  return state;
}

/** What we know of an occasion right now: the view, null when the server has none, undefined when not asked yet. */
export function occasionById(id: string): OccasionView | null | undefined {
  if (state.current?.id === id) return state.current;
  const own = state.owned.find((o) => o.id === id);
  if (own) return own;
  return fetched.get(id);
}

/** GET /api/occasions/:id once per id (published or retired occasions); null when it fails. */
export function fetchOccasion(id: string): Promise<OccasionView | null> {
  const known = occasionById(id);
  if (known !== undefined) return Promise.resolve(known);
  if (!ID.test(id)) { fetched.set(id, null); return Promise.resolve(null); }
  const running = inFlight.get(id);
  if (running) return running;
  const p = (async () => {
    let view: OccasionView | null = null;
    try {
      const r = await fetch(`/api/occasions/${encodeURIComponent(id)}`, { credentials: "same-origin" });
      if (r && r.ok) view = safeOccasion(JSON.parse(await r.text()));
    } catch { view = null; }
    fetched.set(id, view);
    inFlight.delete(id);
    emit();
    return view;
  })();
  inFlight.set(id, p);
  return p;
}

/** Looks the occasion up, asking the server in the background when it is unknown. */
export function resolveOccasion(id: string): OccasionView | null | undefined {
  const v = occasionById(id);
  if (v === undefined) void fetchOccasion(id);
  return v;
}

/** Re-renders when the occasions change (a fetch landed, the look switched). */
export function useOccasionsTick(): number {
  const [n, setN] = useState(0);
  useEffect(() => { const on = () => setN((x) => x + 1); window.addEventListener(EVENT, on); return () => window.removeEventListener(EVENT, on); }, []);
  return n;
}

export interface UseOccasions extends OccasionsState {
  /** The occasion by id (fetched on demand): undefined while it loads, null when there is none. */
  byId: (id: string) => OccasionView | null | undefined;
}
export function useOccasions(): UseOccasions {
  useOccasionsTick();
  return { current: state.current, owned: state.owned, byId: resolveOccasion };
}

/** One occasion by id for a component (fetched on demand). */
export function useOccasion(id: string | null | undefined): OccasionView | null | undefined {
  useOccasionsTick();
  const v = id ? occasionById(id) : null;
  useEffect(() => { if (id && occasionById(id) === undefined) void fetchOccasion(id); }, [id]);
  return v;
}

/** The current occasion while it runs, for a player who keeps occasion looks on (`on` is prefs.occasionLooks). */
export function liveOccasion(on: boolean | undefined): OccasionView | null {
  const c = state.current;
  return c && c.live !== false && on !== false ? c : null;
}

/* ------------------------------------------------------------------ look */

/**
 * The occasion's tokens as CSS: the dark set on <html data-occasion>, and the light set when the theme there is a
 * light base (Sea glass, Reed), so a theme change (Auto at dusk, Appearance) never leaves the wrong set in place.
 * Only names in OCCASION_TOKENS with #RRGGBB values. `base` is kept for callers that ask for one set only.
 */
export function occasionCss(o: OccasionView, base?: string): string {
  if (!ID.test(o.id)) return "";
  const decls = (set: Record<string, string> | undefined) => Object.entries(set ?? {}).filter(([k, v]) => (OCCASION_TOKENS as readonly string[]).includes(k) && HEX.test(v)).map(([k, v]) => `${k}: ${v};`);
  // D-204: an occasion that recolours the accent recolours the main button with it (the button is the accent's fill), so
  // a pack never leaves the theme's lime buttons beside its own gold. The words on it are midnight or white, whichever
  // reads better on that accent.
  const withBead = (set: Record<string, string> | undefined) => {
    const out = decls(set), a = set?.["--color-accent"];
    if (a && HEX.test(a)) {
      const deep = set?.["--color-accent-deep"];
      out.push(`--color-bead: ${a};`, `--color-bead-edge: ${deep && HEX.test(deep) ? deep : a};`, `--color-on-bead: ${onAccent(a)};`);
    }
    return out;
  };
  const dark = withBead(o.look.dark);
  const light = withBead(o.look.light);
  // Doubled :root wins over the built-in theme ([data-theme]).
  const at = `:root:root[data-occasion="${o.id}"]`;
  // D-210: DIGU Classic keeps its own colours (lime buttons included) during an occasion; the occasion shows in its
  // table, card back, stickers, bunting, banner and greeting. The colour sets still apply over the older themes.
  if (base !== undefined) { if (CLASSIC_THEMES.has(base)) return ""; const one = LIGHT_BASES.has(base) ? light : dark; return one.length ? `${at} {\n  ${one.join("\n  ")}\n}` : ""; }
  const rules: string[] = [];
  const notClassic = [...CLASSIC_THEMES].map((b) => `:not([data-theme="${b}"])`).join("");
  // The dark set only over dark themes, so tokens it sets and the light set doesn't never leak into a light theme.
  if (dark.length) rules.push(`${at}${[...LIGHT_BASES].map((b) => `:not([data-theme="${b}"])`).join("")}${notClassic} {\n  ${dark.join("\n  ")}\n}`);
  const lightOld = [...LIGHT_BASES].filter((b) => !CLASSIC_THEMES.has(b));
  if (light.length && lightOld.length) rules.push(`${lightOld.map((b) => `${at}[data-theme="${b}"]`).join(", ")} {\n  ${light.join("\n  ")}\n}`);
  return rules.join("\n");
}

/** WCAG relative luminance of #RRGGBB. */
function luminance(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}
/** D-204: midnight green or white for words on an occasion's accent, whichever has more contrast. */
export function onAccent(hex: string): string {
  const l = luminance(hex), ink = luminance("#0D2B24");
  return (l + 0.05) / (ink + 0.05) >= 1.05 / (l + 0.05) ? "#0D2B24" : "#FFFFFF";
}

/** Applies (or removes) the current occasion's tokens over the theme now on <html>. Call after the theme is applied. */
export function applyOccasionLook(enabled: boolean): void {
  const changed = looksOn !== enabled;
  looksOn = enabled;
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const occ = liveOccasion(enabled);
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  const css = occ ? occasionCss(occ) : "";
  if (occ && css) {
    root.dataset.occasion = occ.id;
    if (!style) { style = document.createElement("style"); style.id = STYLE_ID; document.head.appendChild(style); }
    style.textContent = css;
  } else {
    delete root.dataset.occasion;
    if (style) style.textContent = "";
  }
  if (changed) emit();
}

/* ------------------------------------------------------------------ items */

/** An occasion's table in the shape every table design has, so the table, the pickers and the Shop draw it the same way. */
export function occasionDesign(o: OccasionView): TableDesign {
  return { id: o.table.designId || `occ-${o.id}`, name: o.table.name, story: o.table.story, art: { ...o.table.art }, back: o.back.itemId || `back-occ-${o.id}`, tokens: { ...o.table.tokens }, swatch: { ...o.table.swatch } };
}

/** Any design id: a built-in one, or `occ-<id>` from the occasions (Lagoon while it is unknown or gone). */
export function resolveDesign(id: string | null | undefined): TableDesign {
  if (isOccasionDesign(id)) {
    const o = resolveOccasion(occasionOfDesign(id)!);
    return o ? occasionDesign(o) : tableDesign(DEFAULT_TABLE_DESIGN);
  }
  return tableDesign(id);
}

/** Owned occasion tables, newest first as the server lists them. */
export const ownedOccasionDesigns = (): TableDesign[] => state.owned.map(occasionDesign);

export const isOccasionBack = (itemId: string | null | undefined): itemId is string => !!itemId && /^back-occ-[a-z][a-z0-9-]{2,31}$/.test(itemId);
/** The image of `back-occ-<id>`, or null while the occasion is unknown (asked for in the background). */
export function occasionBackSrc(itemId: string): string | null {
  const o = resolveOccasion(itemId.slice("back-occ-".length));
  return o ? o.back.image : null;
}

/** `occ-<occasion>-<emoji>` → its parts (the emoji id has no dashes, so the last dash splits them). */
export function parseOccasionEmoji(id: string): { occasion: string; emoji: string } | null {
  if (!/^occ-[a-z][a-z0-9-]{2,31}-[a-z][a-z0-9]{1,23}$/.test(id)) return null;
  const cut = id.lastIndexOf("-");
  return { occasion: id.slice(4, cut), emoji: id.slice(cut + 1) };
}
export type OccasionEmoji = OccasionView["emoji"]["items"][number];
/** The emoji for a reaction id: the item, null when it can't be drawn, undefined while its occasion loads. */
export function occasionEmoji(id: string): OccasionEmoji | null | undefined {
  const p = parseOccasionEmoji(id);
  if (!p) return null;
  const o = resolveOccasion(p.occasion);
  if (o === undefined) return undefined;
  return o?.emoji.items.find((e) => e.id === p.emoji) ?? null;
}
